import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Descriptions,
  Progress,
  Space,
  Tag,
  Upload,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import {
  createCustomerConfigImportTask,
  downloadCustomerConfigImportFailures,
  downloadSystemFile,
  getCustomerConfigImportTask,
} from '@/services/ant-design-pro/api';
import { downloadBlob } from '@/utils/download';

export type ConfigExcelImportPanelProps = {
  /** 下载模版：GET /admin/system/download?fileTypeCode= */
  templateFileTypeCode: string;
  /** 上传类型，随文件一并提交 */
  uploadType: string;
  /** 下载保存的文件名 */
  templateFileName: string;
  /** 说明文案 */
  description?: React.ReactNode;
  onSuccess?: () => void;
};

const POLL_INTERVAL_MS = 2000;

const TERMINAL_STATUSES = new Set(['SUCCESS', 'PARTIAL_SUCCESS', 'FAILED']);

const STATUS_META: Record<
  string,
  { label: string; color: string }
> = {
  PENDING: { label: '等待处理', color: 'default' },
  PARSING: { label: '解析中', color: 'processing' },
  PROCESSING: { label: '写入中', color: 'processing' },
  SUCCESS: { label: '全部成功', color: 'success' },
  PARTIAL_SUCCESS: { label: '部分成功', color: 'warning' },
  FAILED: { label: '失败', color: 'error' },
};

const isTerminalStatus = (status?: string) =>
  !!status && TERMINAL_STATUSES.has(status);

/**
 * 客户配置批量 Excel 公共面板：下载后端模版 + 异步上传任务 + 轮询进度。
 * 通过 templateFileTypeCode / uploadType 区分自动审批与测算开关、默认配置与场景规则。
 */
const ConfigExcelImportPanel: React.FC<ConfigExcelImportPanelProps> = ({
  templateFileTypeCode,
  uploadType,
  templateFileName,
  description,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [downloading, setDownloading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingFailures, setDownloadingFailures] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [task, setTask] = useState<API.CustomerConfigImportTaskVO | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeTaskIdRef = useRef<string | null>(null);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    activeTaskIdRef.current = null;
  };

  useEffect(() => () => stopPolling(), []);

  const handleTaskFinished = (detail: API.CustomerConfigImportTaskVO) => {
    const successCount = detail.successCount ?? 0;
    const failureCount = detail.failureCount ?? 0;
    const totalCount = detail.totalCount ?? successCount + failureCount;

    if (successCount > 0) onSuccess?.();

    if (detail.status === 'SUCCESS') {
      message.success(
        `导入完成：共 ${totalCount} 行，全部成功 ${successCount} 行`,
      );
      return;
    }

    if (detail.status === 'PARTIAL_SUCCESS') {
      message.warning(
        `导入完成：共 ${totalCount} 行，成功 ${successCount} 行，失败 ${failureCount} 行`,
      );
      return;
    }

    const reason = detail.errorMessage?.trim();
    message.error(
      reason
        ? `导入失败：${reason}`
        : `导入失败：共 ${totalCount} 行，失败 ${failureCount} 行`,
    );
  };

  const pollTask = async (taskId: string) => {
    if (activeTaskIdRef.current !== taskId) return;

    try {
      const response = await getCustomerConfigImportTask(taskId);
      if (!response.success || !response.result) {
        throw new Error(response.message || '查询导入任务失败');
      }
      const detail = response.result;
      setTask(detail);

      if (isTerminalStatus(detail.status)) {
        stopPolling();
        setSubmitting(false);
        handleTaskFinished(detail);
        return;
      }

      pollTimerRef.current = setTimeout(() => {
        void pollTask(taskId);
      }, POLL_INTERVAL_MS);
    } catch (error) {
      stopPolling();
      setSubmitting(false);
      message.error(
        error instanceof Error ? error.message : '查询导入任务失败',
      );
    }
  };

  const startPolling = (taskId: string) => {
    stopPolling();
    activeTaskIdRef.current = taskId;
    void pollTask(taskId);
  };

  const resetImportState = () => {
    stopPolling();
    setTask(null);
    setSubmitting(false);
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const blob = await downloadSystemFile({
        fileTypeCode: templateFileTypeCode,
      });
      downloadBlob(blob, templateFileName);
      message.success('模版下载成功');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '模版下载失败');
    } finally {
      setDownloading(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      message.error('请先选择 Excel 文件');
      return;
    }
    resetImportState();
    setSubmitting(true);
    try {
      const response = await createCustomerConfigImportTask(file, uploadType);
      if (!response.success || !response.result?.taskId) {
        throw new Error(response.message || '创建导入任务失败');
      }
      const created = response.result;
      setTask(created);
      message.info(created.message || '任务已创建，正在后台处理');
      startPolling(String(created.taskId));
    } catch (error) {
      setSubmitting(false);
      message.error(
        error instanceof Error ? error.message : '创建导入任务失败',
      );
    }
  };

  const handleDownloadFailures = async () => {
    if (!task?.taskId) return;
    if (
      !isTerminalStatus(task.status) ||
      !(task.failureCount && task.failureCount > 0) ||
      !task.failureFileReady
    ) {
      message.warning('失败明细尚未就绪，请稍后再试');
      return;
    }
    setDownloadingFailures(true);
    try {
      const blob = await downloadCustomerConfigImportFailures(String(task.taskId));
      const baseName = task.originalFileName?.replace(/\.(xlsx|xls)$/i, '') || '导入失败明细';
      downloadBlob(blob, `${baseName}-失败明细.xlsx`);
      message.success('失败明细已下载');
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : '下载失败明细失败',
      );
    } finally {
      setDownloadingFailures(false);
    }
  };

  const statusMeta = STATUS_META[task?.status || ''] || {
    label: task?.status || '-',
    color: 'default',
  };
  const running = submitting && !isTerminalStatus(task?.status);
  const canDownloadFailures =
    !!task &&
    isTerminalStatus(task.status) &&
    (task.failureCount ?? 0) > 0 &&
    !!task.failureFileReady;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {description ? <div>{description}</div> : null}
      <Space wrap>
        <Button
          icon={<DownloadOutlined />}
          loading={downloading}
          disabled={running}
          onClick={handleDownloadTemplate}
        >
          下载模版
        </Button>
        <Upload
          accept=".xlsx,.xls"
          maxCount={1}
          showUploadList={!!file}
          disabled={running}
          beforeUpload={(nextFile) => {
            if (!/\.(xlsx|xls)$/i.test(nextFile.name)) {
              message.error('只能上传 Excel 文件');
              return Upload.LIST_IGNORE;
            }
            if (nextFile.size > 10 * 1024 * 1024) {
              message.error('Excel 文件大小不能超过 10MB');
              return Upload.LIST_IGNORE;
            }
            setFile(nextFile);
            resetImportState();
            return false;
          }}
          onRemove={() => {
            setFile(null);
            resetImportState();
          }}
        >
          <Button icon={<UploadOutlined />} disabled={running}>
            选择 Excel
          </Button>
        </Upload>
        <Button
          type="primary"
          loading={running}
          disabled={!file || running}
          onClick={handleImport}
        >
          确认导入
        </Button>
      </Space>

      {file && !task ? (
        <div style={{ color: '#666' }}>已选择：{file.name}</div>
      ) : null}

      {task ? (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type={
              task.status === 'SUCCESS'
                ? 'success'
                : task.status === 'PARTIAL_SUCCESS'
                  ? 'warning'
                  : task.status === 'FAILED'
                    ? 'error'
                    : 'info'
            }
            showIcon
            message={
              <Space>
                <span>导入任务</span>
                <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
                {task.taskId ? (
                  <span style={{ color: '#666' }}>任务号：{task.taskId}</span>
                ) : null}
              </Space>
            }
            description={
              task.errorMessage ||
              task.message ||
              (running ? 'Excel 正在后台解析并写入配置，请稍候…' : undefined)
            }
          />

          {running || (task.progressPercent != null && task.progressPercent > 0) ? (
            <Progress
              percent={Math.max(0, Math.min(100, task.progressPercent ?? 0))}
              status={
                task.status === 'FAILED'
                  ? 'exception'
                  : isTerminalStatus(task.status)
                    ? 'success'
                    : 'active'
              }
            />
          ) : null}

          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="文件名">
              {task.originalFileName || file?.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="进度">
              {task.processedCount ?? 0}/{task.totalCount ?? 0}
              {task.pendingCount != null ? `（待处理 ${task.pendingCount}）` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="成功行数">
              {task.successCount ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label="失败行数">
              {task.failureCount ?? 0}
              {task.parseFailureCount != null
                ? `（解析失败 ${task.parseFailureCount}）`
                : ''}
            </Descriptions.Item>
          </Descriptions>

          {canDownloadFailures ? (
            <Button
              icon={<DownloadOutlined />}
              loading={downloadingFailures}
              onClick={handleDownloadFailures}
            >
              下载失败明细
            </Button>
          ) : null}
        </Space>
      ) : null}
    </Space>
  );
};

export default ConfigExcelImportPanel;
